import type { BirthInputDraft, BirthInputValidationIssue, ValidatedBirthInput } from './birthInput.js';
import { shouldOfferBirthTimeComparison, validateBirthInput } from './birthInput.js';
import type { NormalizedChart, NormalizedHoroscope } from './domain.js';
import type { InterpretationRulePack, TemplatePack } from './interpretation.js';
import { DEFAULT_NATAL_TOPICS, generateHoroscopeReport, generateNatalReport } from './interpretation.js';
import { buildResultSummary } from './resultSummary.js';
import type { TopicId } from './rules.js';
import { candidateTimeIndices, compareNormalizedCharts } from './chartComparison.js';
import { compareBirthTimeInterpretations } from './interpretationComparison.js';
import { assertNormalizedChartIntegrity } from './chartIntegrity.js';
import { assertNormalizedHoroscopeContract } from './horoscopeContractValidation.js';
import { assertCompletePalaceStemTransformations } from './transformationEvidence.js';
import type { ChronologyResolution, ChronologyRouter, ChronologySelection } from './chronology.js';
import { assertChronologySelectionCanCalculate, parseAstronomicalDate } from './chronology.js';

export interface WorkflowChronologyBinding {
  router: ChronologyRouter;
  selection: ChronologySelection;
}

export interface ApplicationWorkflowOptions {
  chronology?: WorkflowChronologyBinding;
}

export interface WorkflowChronologyMetadata {
  birth: ChronologyResolution;
  target?: ChronologyResolution;
}

function resolveStandardChronology(
  calendar: 'solar' | 'lunar',
  date: string,
  binding?: WorkflowChronologyBinding,
): ChronologyResolution | undefined {
  if (!binding) return undefined;
  assertChronologySelectionCanCalculate(binding.router, binding.selection);
  if (binding.selection.mode !== 'standard') {
    throw new Error('Extended Chronology requires a dedicated extended calculation engine and cannot use the V1 Standard CalculationEngine');
  }
  return binding.router.resolve('standard', { calendar, date: parseAstronomicalDate(date) });
}

export interface CalculationEngine {
  calculateNatal(input: ValidatedBirthInput): Promise<NormalizedChart>;
  calculateHoroscope(input: ValidatedBirthInput, targetDate: string, targetTimeIndex: number): Promise<NormalizedHoroscope>;
  calculateNatalAndHoroscope?: (
    input: ValidatedBirthInput,
    targetDate: string,
    targetTimeIndex: number,
  ) => Promise<{ chart: NormalizedChart; horoscope: NormalizedHoroscope }>;
}

export type WorkflowFailure = { ok:false; issues:readonly BirthInputValidationIssue[] };
export interface NatalWorkflowSuccess {
  ok:true;
  chart:NormalizedChart;
  report:ReturnType<typeof generateNatalReport>;
  summary:ReturnType<typeof buildResultSummary>;
  offerBirthTimeComparison:boolean;
  chronology?: WorkflowChronologyMetadata;
}
export interface HoroscopeWorkflowSuccess extends NatalWorkflowSuccess {
  horoscope:NormalizedHoroscope;
  horoscopeReport:ReturnType<typeof generateHoroscopeReport>;
}

export async function runNatalWorkflow(inputDraft:BirthInputDraft, engine:CalculationEngine, rulePack:InterpretationRulePack, templatePack:TemplatePack, topics:readonly TopicId[]=DEFAULT_NATAL_TOPICS, options:ApplicationWorkflowOptions={}):Promise<WorkflowFailure|NatalWorkflowSuccess>{
  const validated=validateBirthInput(inputDraft);
  if(!validated.ok||!validated.value)return{ok:false,issues:validated.issues};
  const birthChronology=resolveStandardChronology(validated.value.calendar,validated.value.date,options.chronology);
  const chart=await engine.calculateNatal(validated.value);
  assertNormalizedChartIntegrity(chart);
  assertCompletePalaceStemTransformations(chart);
  const report=generateNatalReport(chart,rulePack,templatePack,topics);
  return{ok:true,chart,report,summary:buildResultSummary(report.topics),offerBirthTimeComparison:shouldOfferBirthTimeComparison(validated.value),...(birthChronology?{chronology:{birth:birthChronology}}:{})};
}

export async function runHoroscopeWorkflow(inputDraft:BirthInputDraft,targetDate:string,targetTimeIndex:number,engine:CalculationEngine,rulePack:InterpretationRulePack,templatePack:TemplatePack,natalTopics:readonly TopicId[]=DEFAULT_NATAL_TOPICS,horoscopeTopics:readonly TopicId[]=['timing'],options:ApplicationWorkflowOptions={}):Promise<WorkflowFailure|HoroscopeWorkflowSuccess>{
  const validated=validateBirthInput(inputDraft);
  if(!validated.ok||!validated.value)return{ok:false,issues:validated.issues};
  const birthChronology=resolveStandardChronology(validated.value.calendar,validated.value.date,options.chronology);
  const targetChronology=resolveStandardChronology('solar',targetDate,options.chronology);
  const bundle=engine.calculateNatalAndHoroscope
    ? await engine.calculateNatalAndHoroscope(validated.value,targetDate,targetTimeIndex)
    : {chart:await engine.calculateNatal(validated.value),horoscope:await engine.calculateHoroscope(validated.value,targetDate,targetTimeIndex)};
  assertNormalizedChartIntegrity(bundle.chart);
  assertCompletePalaceStemTransformations(bundle.chart);
  assertNormalizedHoroscopeContract(bundle.horoscope);
  if(bundle.chart.calculation.chartFingerprint!==bundle.horoscope.natalChartFingerprint)throw new Error('Workflow rejected horoscope bound to a different natal chart');
  const report=generateNatalReport(bundle.chart,rulePack,templatePack,natalTopics);
  const horoscopeReport=generateHoroscopeReport(bundle.chart,bundle.horoscope,rulePack,templatePack,horoscopeTopics);
  return{ok:true,chart:bundle.chart,horoscope:bundle.horoscope,report,horoscopeReport,summary:buildResultSummary(report.topics),offerBirthTimeComparison:shouldOfferBirthTimeComparison(validated.value),...(birthChronology&&targetChronology?{chronology:{birth:birthChronology,target:targetChronology}}:{})};
}

export interface BirthTimeCandidateResult {
  timeIndex:number;
  chart:NormalizedChart;
}
export interface BirthTimeWorkflowComparison {
  model:'BIRTH-TIME-WORKFLOW-0.1.0';
  centerTimeIndex:number;
  candidates:readonly BirthTimeCandidateResult[];
  adjacentComparisons:readonly {
    beforeTimeIndex:number;
    afterTimeIndex:number;
    structural:ReturnType<typeof compareNormalizedCharts>;
    interpretation:ReturnType<typeof compareBirthTimeInterpretations>;
  }[];
  disclosure:string;
  chronology?: WorkflowChronologyMetadata;
}
export async function compareBirthTimeWindow(inputDraft:BirthInputDraft,engine:CalculationEngine,rulePack:InterpretationRulePack,templatePack:TemplatePack,topics:readonly TopicId[]=['personality','career','wealth','relationship'],radius=1,options:ApplicationWorkflowOptions={}):Promise<WorkflowFailure|{ok:true;comparison:BirthTimeWorkflowComparison}>{
  const validated=validateBirthInput(inputDraft);
  if(!validated.ok||!validated.value)return{ok:false,issues:validated.issues};
  const birthChronology=resolveStandardChronology(validated.value.calendar,validated.value.date,options.chronology);
  const indices=candidateTimeIndices(validated.value.timeIndex,radius);
  const candidates:BirthTimeCandidateResult[]=[];
  for(const timeIndex of indices){
    const candidateInput={...validated.value,timeIndex};
    const chart=await engine.calculateNatal(candidateInput);
    assertNormalizedChartIntegrity(chart);
    assertCompletePalaceStemTransformations(chart);
    candidates.push({timeIndex,chart});
  }
  const adjacentComparisons=[];
  for(let i=1;i<candidates.length;i+=1){
    const before=candidates[i-1]!; const after=candidates[i]!;
    adjacentComparisons.push({beforeTimeIndex:before.timeIndex,afterTimeIndex:after.timeIndex,structural:compareNormalizedCharts(before.chart,after.chart),interpretation:compareBirthTimeInterpretations(before.chart,after.chart,topics,rulePack,templatePack)});
  }
  return{ok:true,comparison:{model:'BIRTH-TIME-WORKFLOW-0.1.0',centerTimeIndex:validated.value.timeIndex,candidates,adjacentComparisons,disclosure:'此比較只呈現候選時辰造成的命盤與規則解讀差異，不排序哪個時辰較正確，也不產生準確率。',...(birthChronology?{chronology:{birth:birthChronology}}:{})}};
}

import type { RelationshipContextType } from './relationshipContext.js';
import { buildRelationshipContext } from './relationshipContext.js';
import { generateRelationshipReport, type RelationshipRulePack, type RelationshipTemplatePack } from './relationshipInterpretation.js';
import { relationshipProductionPacks } from './relationshipFormalRules.js';
import { buildRelationshipTimingContext, type RelationshipTimingContextBundle } from './relationshipTimingContext.js';
import { generateRelationshipTimingReport, type GeneratedRelationshipTimingReport } from './relationshipTimingInterpretation.js';

export interface RelationshipWorkflowFailure {
  ok: false;
  personAIssues: readonly BirthInputValidationIssue[];
  personBIssues: readonly BirthInputValidationIssue[];
}

export interface RelationshipWorkflowSuccess {
  ok: true;
  personAChart: NormalizedChart;
  personBChart: NormalizedChart;
  context: Awaited<ReturnType<typeof buildRelationshipContext>>;
  report: ReturnType<typeof generateRelationshipReport>;
  chronology?: Readonly<{ personA: ChronologyResolution; personB: ChronologyResolution }>;
}

export async function runRelationshipWorkflow(
  personAInputDraft: BirthInputDraft,
  personBInputDraft: BirthInputDraft,
  relationshipType: RelationshipContextType,
  engine: CalculationEngine,
  packs?: { rulePack: RelationshipRulePack; templatePack: RelationshipTemplatePack },
  options: ApplicationWorkflowOptions = {},
): Promise<RelationshipWorkflowFailure | RelationshipWorkflowSuccess> {
  const personAValidated = validateBirthInput(personAInputDraft);
  const personBValidated = validateBirthInput(personBInputDraft);
  if (!personAValidated.ok || !personAValidated.value || !personBValidated.ok || !personBValidated.value) {
    return {
      ok: false,
      personAIssues: personAValidated.issues,
      personBIssues: personBValidated.issues,
    };
  }

  const personAChronology=resolveStandardChronology(personAValidated.value.calendar,personAValidated.value.date,options.chronology);
  const personBChronology=resolveStandardChronology(personBValidated.value.calendar,personBValidated.value.date,options.chronology);

  // Keep the calls sequential: iztro config is global state and must not be raced inside one runtime.
  const personAChart = await engine.calculateNatal(personAValidated.value);
  assertNormalizedChartIntegrity(personAChart);
  assertCompletePalaceStemTransformations(personAChart);
  const personBChart = await engine.calculateNatal(personBValidated.value);
  assertNormalizedChartIntegrity(personBChart);
  assertCompletePalaceStemTransformations(personBChart);

  const context = await buildRelationshipContext(personAChart, personBChart, relationshipType);
  const activePacks = packs ?? relationshipProductionPacks(relationshipType);
  const report = generateRelationshipReport(context, activePacks.rulePack, activePacks.templatePack);
  return { ok: true, personAChart, personBChart, context, report, ...(personAChronology&&personBChronology?{chronology:{personA:personAChronology,personB:personBChronology}}:{}) };
}


export interface RelationshipTimingTargetIssue {
  field: 'targetDate' | 'targetTimeIndex';
  code: string;
  message: string;
}

export interface RelationshipTimingWorkflowFailure {
  ok: false;
  personAIssues: readonly BirthInputValidationIssue[];
  personBIssues: readonly BirthInputValidationIssue[];
  targetIssues: readonly RelationshipTimingTargetIssue[];
}

export interface RelationshipTimingWorkflowSuccess {
  ok: true;
  personAChart: NormalizedChart;
  personBChart: NormalizedChart;
  personAHoroscope: NormalizedHoroscope;
  personBHoroscope: NormalizedHoroscope;
  context: Awaited<ReturnType<typeof buildRelationshipContext>>;
  report: ReturnType<typeof generateRelationshipReport>;
  timingContext: RelationshipTimingContextBundle;
  timingReport: GeneratedRelationshipTimingReport;
  chronology?: Readonly<{ personA: ChronologyResolution; personB: ChronologyResolution; target: ChronologyResolution }>;
}

function validateRelationshipTimingTarget(targetDate: string, targetTimeIndex: number): readonly RelationshipTimingTargetIssue[] {
  const issues: RelationshipTimingTargetIssue[] = [];
  const probe = validateBirthInput({ calendar: 'solar', date: targetDate, timeIndex: targetTimeIndex, gender: 'female' });
  for (const issue of probe.issues) {
    if (issue.field === 'date') issues.push({ field: 'targetDate', code: issue.code, message: issue.message });
    if (issue.field === 'timeIndex') issues.push({ field: 'targetTimeIndex', code: issue.code, message: issue.message });
  }
  return Object.freeze(issues);
}

async function calculateValidatedNatalAndHoroscope(
  input: ValidatedBirthInput,
  targetDate: string,
  targetTimeIndex: number,
  engine: CalculationEngine,
): Promise<{ chart: NormalizedChart; horoscope: NormalizedHoroscope }> {
  const bundle = engine.calculateNatalAndHoroscope
    ? await engine.calculateNatalAndHoroscope(input, targetDate, targetTimeIndex)
    : { chart: await engine.calculateNatal(input), horoscope: await engine.calculateHoroscope(input, targetDate, targetTimeIndex) };
  assertNormalizedChartIntegrity(bundle.chart);
  assertCompletePalaceStemTransformations(bundle.chart);
  assertNormalizedHoroscopeContract(bundle.horoscope);
  if (bundle.chart.calculation.chartFingerprint !== bundle.horoscope.natalChartFingerprint) {
    throw new Error('Relationship timing workflow rejected horoscope bound to a different natal chart');
  }
  return bundle;
}

export async function runRelationshipTimingWorkflow(
  personAInputDraft: BirthInputDraft,
  personBInputDraft: BirthInputDraft,
  relationshipType: RelationshipContextType,
  targetDate: string,
  targetTimeIndex: number,
  engine: CalculationEngine,
  packs?: { rulePack: RelationshipRulePack; templatePack: RelationshipTemplatePack },
  options: ApplicationWorkflowOptions = {},
): Promise<RelationshipTimingWorkflowFailure | RelationshipTimingWorkflowSuccess> {
  const personAValidated = validateBirthInput(personAInputDraft);
  const personBValidated = validateBirthInput(personBInputDraft);
  const targetIssues = validateRelationshipTimingTarget(targetDate, targetTimeIndex);
  if (!personAValidated.ok || !personAValidated.value || !personBValidated.ok || !personBValidated.value || targetIssues.length) {
    return {
      ok: false,
      personAIssues: personAValidated.issues,
      personBIssues: personBValidated.issues,
      targetIssues,
    };
  }

  const personAChronology = resolveStandardChronology(personAValidated.value.calendar, personAValidated.value.date, options.chronology);
  const personBChronology = resolveStandardChronology(personBValidated.value.calendar, personBValidated.value.date, options.chronology);
  const targetChronology = resolveStandardChronology('solar', targetDate, options.chronology);

  // Keep A then B sequential because the V1 iztro adapter wraps global config state.
  const personA = await calculateValidatedNatalAndHoroscope(personAValidated.value, targetDate, targetTimeIndex, engine);
  const personB = await calculateValidatedNatalAndHoroscope(personBValidated.value, targetDate, targetTimeIndex, engine);
  const context = await buildRelationshipContext(personA.chart, personB.chart, relationshipType);
  const activePacks = packs ?? relationshipProductionPacks(relationshipType);
  const report = generateRelationshipReport(context, activePacks.rulePack, activePacks.templatePack);
  const timingContext = await buildRelationshipTimingContext(context, personA.horoscope, personB.horoscope);
  const timingReport = generateRelationshipTimingReport(
    timingContext,
    `${personA.chart.calculation.rulesetId}@${personA.chart.calculation.rulesetVersion}`,
    `${personB.chart.calculation.rulesetId}@${personB.chart.calculation.rulesetVersion}`,
  );

  return {
    ok: true,
    personAChart: personA.chart,
    personBChart: personB.chart,
    personAHoroscope: personA.horoscope,
    personBHoroscope: personB.horoscope,
    context,
    report,
    timingContext,
    timingReport,
    ...(personAChronology && personBChronology && targetChronology
      ? { chronology: { personA: personAChronology, personB: personBChronology, target: targetChronology } }
      : {}),
  };
}
